// RegionNewBadgeSeen — 範囲の道具 (範囲の移動・コピー) の入口ボタン (Modern 左ツール列 /
// Classic 道具フライアウト / スマホのブラシ設定シート) の「New!」バッジを
// 「初回だけ」出すための seen フラグ永続化 (infrastructure 層、region-move-2026-10-15.md)。
//
// `customCursorNewBadgeSeen.ts` / `undoBadgeSeen.ts` / `pastCanvasSeen.ts` と同流儀で、
// 別 localStorage key で独立管理する。消える条件は「一度でも範囲の道具を選んだ」
// (= どの入口・どのレイアウトからでも。レイアウトを切り替えても共有される単一 store)。
// 不在 / storage 不可は「未 seen」(= まだ出す)。例外を throw しない。
//
// Tests: regionNewBadgeSeen.test.ts (storage-injection style)。

/** localStorage キー: 範囲の New! バッジを消したか (= 一度でも範囲の道具を選んだか). */
export const REGION_NEW_BADGE_SEEN_KEY = 'oekaki:region-new-seen';

/** Minimal Storage shape this module uses. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface RegionNewBadgeSeenStore {
  hasSeen(): boolean;
  markSeen(): void;
  clear(): void;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:region-new-seen__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateRegionNewBadgeSeenStoreOptions {
  /** Override storage. `null` forces the no-op path. Default: probe localStorage. */
  storage?: StorageLike | null;
}

export const createRegionNewBadgeSeenStore = (
  opts: CreateRegionNewBadgeSeenStoreOptions = {},
): RegionNewBadgeSeenStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    hasSeen() {
      if (storage === null) return false;
      try {
        return storage.getItem(REGION_NEW_BADGE_SEEN_KEY) !== null;
      } catch {
        return false;
      }
    },
    markSeen() {
      if (storage === null) return;
      try {
        storage.setItem(REGION_NEW_BADGE_SEEN_KEY, '1');
      } catch {
        // QuotaExceeded / SecurityError 等 — 出し直しになるだけで害はない.
      }
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(REGION_NEW_BADGE_SEEN_KEY);
      } catch {
        // ignore
      }
    },
  };
};
